import * as yup from 'yup';
import { restaurantTagValidationConstants } from '../Constants/validationConstants';

// Maps yup error paths to the existing restaurantTagValidationConstants keys
// so consumers using getErrorMessages(KEY_*) keep working unchanged.
const PATH_KEY_MAP = {
  enName: restaurantTagValidationConstants.KEY_EN_NAME,
  tcName: restaurantTagValidationConstants.KEY_TC_NAME,
  scName: restaurantTagValidationConstants.KEY_SC_NAME,
  orderSequence: restaurantTagValidationConstants.KEY_ORDER_SEQUENCE,
  enImage: restaurantTagValidationConstants.KEY_EN_IMAGE,
  'enImage.cosFileUrl': restaurantTagValidationConstants.KEY_EN_IMAGE,
  tcImage: restaurantTagValidationConstants.KEY_TC_IMAGE,
  'tcImage.cosFileUrl': restaurantTagValidationConstants.KEY_TC_IMAGE,
  scImage: restaurantTagValidationConstants.KEY_SC_IMAGE,
  'scImage.cosFileUrl': restaurantTagValidationConstants.KEY_SC_IMAGE,
  status: restaurantTagValidationConstants.KEY_STATUS,
  'name.en': restaurantTagValidationConstants.KEY_EN_NAME,
  'name.zh-HK': restaurantTagValidationConstants.KEY_TC_NAME,
  'name.zh-TW': restaurantTagValidationConstants.KEY_TC_NAME,
  'name.zh-CN': restaurantTagValidationConstants.KEY_SC_NAME,
  'type.code': restaurantTagValidationConstants.KEY_TYPE_CODE,
};

const imageSchema = (errorMessage) => yup.object().shape({
  cosFileUrl: yup.string().required(errorMessage),
}).required(errorMessage);

// Missing: !orderSequence && orderSequence !== 0
// Invalid: orderSequence < 0 || !Number.isInteger(Number(orderSequence))
const orderSequenceSchema = yup.mixed().test(
  'orderSequence',
  'Invalid Order Sequence',
  function (value) {
    if (!value && value !== 0) {
      return this.createError({ path: this.path, message: 'Missing Order Sequence' });
    }
    if (value < 0 || !Number.isInteger(Number(value))) {
      return this.createError({ path: this.path, message: 'Invalid Order Sequence' });
    }
    return true;
  }
);

// Same as orderSequenceSchema but preserves the original handleValidateEditRestaurantTagMap
// behaviour, which reads `sequence` (not `orderSequence`) for the negative check.
const orderSequenceMapSchema = yup.mixed().test(
  'orderSequence',
  'Invalid Order Sequence',
  function (value) {
    if (!value && value !== 0) {
      return this.createError({ path: this.path, message: 'Missing Order Sequence' });
    }
    if (this.parent.sequence < 0 || !Number.isInteger(Number(value))) {
      return this.createError({ path: this.path, message: 'Invalid Order Sequence' });
    }
    return true;
  }
);

const statusSchema = yup.mixed().test(
  'status',
  'Missing Status',
  (value) => !!value
);

const baseTagSchema = {
  enName: yup.string().required('Missing EN Name'),
  tcName: yup.string().required('Missing TC Name'),
  scName: yup.string().required('Missing SC Name'),
};

const createSchema = yup.object().shape({
  ...baseTagSchema,
  orderSequence: orderSequenceSchema,
  enImage: imageSchema('Missing EN Image'),
  tcImage: imageSchema('Missing TC Image'),
  scImage: imageSchema('Missing SC Image'),
});

const editSchema = yup.object().shape({
  ...baseTagSchema,
  orderSequence: orderSequenceSchema,
  enImage: imageSchema('Missing EN Image'),
  tcImage: imageSchema('Missing TC Image'),
  scImage: imageSchema('Missing SC Image'),
  status: statusSchema,
});

const editMapSchema = yup.object().shape({
  ...baseTagSchema,
  orderSequence: orderSequenceMapSchema,
  status: statusSchema,
});

const cloneSchema = yup.object().shape({
  name: yup.object().shape({
    en: yup.string().required('Missing EN Name'),
    'zh-HK': yup.string().required('Missing TC Name'),
    'zh-TW': yup.string().required('Missing TC Name'),
    'zh-CN': yup.string().required('Missing SC Name'),
  }),
  type: yup.object().shape({
    code: yup.string().required('Missing Type Code'),
  }),
});

class RestaurantTagValidation {

  version;
  errorMap;
  restaurantTagErrorMap;
  valid;

  constructor() {

    if (!RestaurantTagValidation.instance) {
      this.version = 0;
      this.valid = true;
      this.errorMap = new Map();
      this.restaurantTagErrorMap = new Map();
      RestaurantTagValidation.instance = this;
    }
    return RestaurantTagValidation.instance;
  }

  validateEditRestaurantTag(restaurantTag) {
    this.resetErrorMessages();
    this.handleValidate(editSchema, restaurantTag);
    this.version++;
  }

  validateCreateRestaurantTag(restaurantTag) {
    this.resetErrorMessages();
    this.handleValidate(createSchema, restaurantTag);
    this.version++;
  }

  validateCreateRestaurantTagForClone(newTagPayload) {
    this.resetErrorMessages();
    const tag = (newTagPayload && newTagPayload.tag) || {};
    this.handleValidate(cloneSchema, { name: tag.name || {}, type: tag.type || {} });
    this.version++;
  }

  validateEditRestaurantTagMap(restaurantTag) {
    this.resetErrorMessages();
    this.handleValidate(editMapSchema, restaurantTag);
    this.version++;
  }

  resetErrorMessages() {
    this.errorMap = new Map();
    this.restaurantTagErrorMap = new Map();
    this.valid = true;
  }

  getVersion() {
    return this.version;
  }

  isValid() {
    return this.valid;
  }

  getErrorMessages(key, uniqueCode) {
    if (!uniqueCode) {
      return (this.restaurantTagErrorMap.has(key)) ? this.restaurantTagErrorMap.get(key) : [];
    }
  }

  handleValidateEditRestaurantTag(restaurantTag) {
    if (!restaurantTag.enName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_EN_NAME, "Missing EN Name");
    }
    if (!restaurantTag.tcName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_TC_NAME, "Missing TC Name");
    }
    if (!restaurantTag.scName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_SC_NAME, "Missing SC Name");
    }
    if (!restaurantTag.orderSequence && restaurantTag.orderSequence !== 0) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_ORDER_SEQUENCE, "Missing Order Sequence");
    } else if (restaurantTag.orderSequence < 0 || !Number.isInteger(Number(restaurantTag.orderSequence))) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_ORDER_SEQUENCE, "Invalid Order Sequence");
    }
    if (!restaurantTag.enImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_EN_IMAGE, "Missing EN Image");
    }
    if (!restaurantTag.tcImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_TC_IMAGE, "Missing TC Image");
    }
    if (!restaurantTag.scImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_SC_IMAGE, "Missing SC Image");
    }
    if (!restaurantTag.status) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_STATUS, "Missing Status");
    }
  }

  handleValidateCreateRestaurantTag(restaurantTag) {
    if (!restaurantTag.enName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_EN_NAME, "Missing EN Name");
    }
    if (!restaurantTag.tcName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_TC_NAME, "Missing TC Name");
    }
    if (!restaurantTag.scName) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_SC_NAME, "Missing SC Name");
    }
    if (!restaurantTag.orderSequence && restaurantTag.orderSequence !== 0) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_ORDER_SEQUENCE, "Missing Order Sequence");
    } else if (restaurantTag.orderSequence < 0 || !Number.isInteger(Number(restaurantTag.orderSequence))) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_ORDER_SEQUENCE, "Invalid Order Sequence");
    }
    if (!restaurantTag.enImage || !restaurantTag.enImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_EN_IMAGE, "Missing EN Image");
    }
    if (!restaurantTag.tcImage || !restaurantTag.tcImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_TC_IMAGE, "Missing TC Image");
    }
    if (!restaurantTag.scImage || !restaurantTag.scImage.cosFileUrl) {
      this.setRestaurantTagErrorMapMessages(restaurantTagValidationConstants.KEY_SC_IMAGE, "Missing SC Image");
    }
  }


  setRestaurantTagErrorMapMessages(key, errorMessages, valid = false) {
    this.restaurantTagErrorMap.set(key, errorMessages);
    // console.log("restaurantTagErrorMap", this.restaurantTagErrorMap)
    if (!valid) {
      this.valid = false;
    }
  }


  handleValidate(schema, data) {
    try {
      schema.validateSync(data, { abortEarly: false });
    } catch (err) {
      this.valid = false;
      const errors = (err && err.inner && err.inner.length > 0) ? err.inner : [err];
      errors.forEach((e) => {
        const key = PATH_KEY_MAP[e.path];
        if (!key || this.restaurantTagErrorMap.has(key)) return;
        this.restaurantTagErrorMap.set(key, e.message);
      });
    }
  }

}

const validation = new RestaurantTagValidation();
export default validation;
